import { Formik } from 'formik';
import React, { useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { FormGroup, InlineNotification, Stack } from '@carbon/react';
import { FormFieldUpload } from 'src/components/Form/FormFieldUpload';
import { FormikTextInput } from 'src/components/Form/FormikInputs';
import { FormikRadioButtonGroup } from 'src/components/Form/FormikRadioButtonGroup';
import { InventoryCategoryEntity } from 'src/services/apis/types';
import { InventoryItemStatusEnum, InventoryItemStatuses } from 'src/utils/inventory';

interface InventoryCategoryFormProps {
  onSubmit: (values: any) => void;
  errorMessage: string | null;
  initialValues: InventoryCategoryEntity | null | undefined;
}

const BaseSchema = z.object({
  name: z.string().max(50).trim(),
});

const InventoryCategoryForm = (props: InventoryCategoryFormProps, ref) => {
  const { t } = useTranslation();
  const formikRef = useRef<any>();

  useImperativeHandle(ref, () => {
    return {
      submitForm: formikRef.current?.submitForm,
      setFieldValue: formikRef.current?.setFieldValue,
      resetForm: () =>
        formikRef.current?.resetForm({
          values: {
            id: '',
            name: '',
            description: '',
            status: InventoryItemStatusEnum.ACTIVE,
            tags: '',
            icon: '',
          },
        }),
    };
  });

  return (
    <div>
      {props.errorMessage && <InlineNotification subtitle={props.errorMessage} />}
      <Formik
        innerRef={formikRef}
        enableReinitialize
        validationSchema={toFormikValidationSchema(BaseSchema)}
        initialValues={{
          id: props.initialValues?.id,
          name: props.initialValues?.name,
          description: props.initialValues?.description,
          status: props.initialValues?.status || InventoryItemStatusEnum.ACTIVE,
          // The entity stores tags as text[], but the field edits (and the page's submit
          // handler splits) a comma-separated string — seeding the raw array made
          // `values.tags.split(',')` throw inside Formik's submit, so editing a category
          // silently did nothing. Same join the item form does.
          tags: props.initialValues?.tags ? props.initialValues?.tags.join(',') : '',
          icon: props.initialValues?.icon,
        }}
        onSubmit={props.onSubmit}
      >
        {({ values, setFieldValue }) => (
          <FormGroup legendText={''}>
            <Stack gap={5}>
              <FormikTextInput name={'name'} label={t('common.name')} />
              <FormikTextInput name={'description'} label={t('common.description')} />
              <FormikTextInput name={'tags'} label={`${t('common.tags')} (${t('common.separateByAComma')})`} />
              <FormFieldUpload
                title={t('common.icon')}
                description={t('common.squareImageRecommended')}
                aspectRatio="1x1"
                cropperAspectRatio={1}
                value={values.icon}
                onChange={(data) => setFieldValue('icon', data.fileId)}
              />
              <FormikRadioButtonGroup
                name={'status'}
                label={t('common.status')}
                value={values.status!}
                useTranslation={true}
                onChange={(value) => setFieldValue('status', value)}
                options={InventoryItemStatuses}
              />
            </Stack>
          </FormGroup>
        )}
      </Formik>
    </div>
  );
};

export default React.forwardRef(InventoryCategoryForm);
